import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { OptimizationResultsWithStats } from '../../types';
import { exportStatisticsToExcel } from '../../utils/exportUtils';

interface StatisticsViewProps {
  results: OptimizationResultsWithStats;
}

const StatisticsView: React.FC<StatisticsViewProps> = ({ results }) => {
  if (!results.statistics) {
    return null;
  }

  const algorithms = ['GA', 'Culture', 'Frog', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'];
  const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;
  const metrics = [
    { key: 'fitness', label: 'Fitness', unit: '', higherBetter: true },
    { key: 'energy', label: 'Energy', unit: ' J', higherBetter: false },
    { key: 'cost', label: 'Cost', unit: '', higherBetter: false },
    { key: 'responseTime', label: 'Response Time', unit: ' ms', higherBetter: false },
    { key: 'executionTime', label: 'Execution Time', unit: ' ms', higherBetter: false },
  ] as const;

  const renderMetricTable = (metricKey: typeof metrics[number]['key']) => {
    const metric = metrics.find(m => m.key === metricKey)!;
    const higherBetter = metric.higherBetter;

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h6" component="div">
            {metric.label} Statistics
          </Typography>
          <Typography variant="caption">
            {higherBetter ? 'Higher values are better' : 'Lower values are better'} • Using MEDIAN as primary comparison
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Algorithm</strong></TableCell>
              <TableCell align="right"><strong>Median</strong></TableCell>
              <TableCell align="right"><strong>± Std Dev</strong></TableCell>
              <TableCell align="right"><strong>Mean</strong></TableCell>
              <TableCell align="right"><strong>Min</strong></TableCell>
              <TableCell align="right"><strong>Max</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {algorithmKeys.map((algKey, idx) => {
              const stats = results.statistics![algKey][metricKey];
              const medianValue = stats.median;
              
              // Find best using MEDIAN (highest if higherBetter, lowest otherwise)
              const allMedians = algorithmKeys.map(k => results.statistics![k][metricKey].median);
              const bestMedian = higherBetter ? Math.max(...allMedians) : Math.min(...allMedians);
              const isBest = Math.abs(medianValue - bestMedian) < 1e-9; // Floating point comparison

              return (
                <TableRow
                  key={algKey}
                  sx={{
                    bgcolor: isBest ? 'success.light' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={isBest ? 600 : 400}>
                      {algorithms[idx]}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={isBest ? 600 : 400}>
                      {stats.median.toFixed(2)}{metric.unit}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">±{stats.stdDev.toFixed(2)}{metric.unit}</TableCell>
                  <TableCell align="right">{stats.mean.toFixed(2)}{metric.unit}</TableCell>
                  <TableCell align="right">{stats.min.toFixed(2)}{metric.unit}</TableCell>
                  <TableCell align="right">{stats.max.toFixed(2)}{metric.unit}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const handleExport = () => {
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `statistics_results_${timestamp}.xlsx`;
      exportStatisticsToExcel(results, filename);
    } catch (error) {
      console.error('Failed to export statistics:', error);
      alert('Failed to export statistics: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TrendingUpIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h2">
            Statistical Analysis
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
        >
          Export Statistics
        </Button>
      </Box>

      {results.runNumber && results.runNumber > 1 && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2">
            <strong>Number of Runs:</strong> {results.runNumber}
            {results.timestamp && (
              <> • <strong>Timestamp:</strong> {new Date(results.timestamp).toLocaleString()}</>
            )}
          </Typography>
        </Box>
      )}

      {metrics.map(metric => (
        <Box key={metric.key}>
          {renderMetricTable(metric.key)}
        </Box>
      ))}

      {/* Composite Score Table */}
      {results.compositeScores && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'secondary.main', color: 'white' }}>
            <Typography variant="h6" component="div">
              Composite Score Statistics
            </Typography>
            <Typography variant="caption">
              Weighted combination: Fitness (50%) + Energy (30%) + Cost (20%) • Higher is better
            </Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Algorithm</strong></TableCell>
                <TableCell align="right"><strong>Median Score</strong></TableCell>
                <TableCell align="right"><strong>Mean Score</strong></TableCell>
                <TableCell align="right"><strong>Std Dev</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {algorithmKeys.map((algKey, idx) => {
                const score = results.compositeScores![algKey];
                if (!score) return null;
                
                const allMedians = algorithmKeys
                  .map(k => results.compositeScores![k]?.median ?? -Infinity)
                  .filter(v => v !== -Infinity);
                const bestMedian = Math.max(...allMedians);
                const isBest = Math.abs(score.median - bestMedian) < 1e-9;

                return (
                  <TableRow
                    key={algKey}
                    sx={{
                      bgcolor: isBest ? 'success.light' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={isBest ? 600 : 400}>
                        {algorithms[idx]}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={isBest ? 600 : 400}>
                        {score.median.toFixed(4)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{score.mean.toFixed(4)}</TableCell>
                    <TableCell align="right">{score.stdDev.toFixed(4)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pareto Analysis Table */}
      {results.paretoAnalysis && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'info.main', color: 'white' }}>
            <Typography variant="h6" component="div">
              Pareto Analysis
            </Typography>
            <Typography variant="caption">
              Pareto-optimal solutions: {results.paretoAnalysis.paretoOptimalSolutions} / {results.paretoAnalysis.totalSolutions}
            </Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Algorithm</strong></TableCell>
                <TableCell align="right"><strong>Pareto-Optimal Count</strong></TableCell>
                <TableCell align="right"><strong>Percentage Contribution</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.paretoAnalysis.algorithmContributions.map((contrib, idx) => {
                const maxCount = Math.max(...results.paretoAnalysis!.algorithmContributions.map(c => c.paretoOptimalCount));
                const isBest = contrib.paretoOptimalCount === maxCount && maxCount > 0;

                return (
                  <TableRow
                    key={contrib.algorithmName}
                    sx={{
                      bgcolor: isBest ? 'success.light' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={isBest ? 600 : 400}>
                        {contrib.algorithmName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={isBest ? 600 : 400}>
                        {contrib.paretoOptimalCount}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{contrib.percentageContribution.toFixed(2)}%</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default StatisticsView;

